import { Controller } from "@hotwired/stimulus";
import "./turbo-stream-actions";
import { HeaderManagementMixin } from "./mixins/header-management";
import { PayoutRequestHandlingMixin } from "./mixins/payout_request-handling";
import { PayoutRequestCancelHandlingMixin } from "./mixins/payout_request-cancel-handling";
import { FooterDialogueManagementMixin } from "./mixins/footer-dialogue-management";
import { SceneNavigationMixin } from "./mixins/scene-navigation";
import { TurboHelpersMixin } from "./mixins/turbo-helpers";
import { PayoutRequestHistoryTabsManagementMixin } from "./mixins/payout_request-history-tabs-management";
import { HarvestHistoryTabsManagementMixin } from "./mixins/harvest_history-tabs-management";
import { EstimatedReturnTabsManagementMixin } from "./mixins/estimated_return-tabs-management";
import { CHARACTER_NAME } from "./constants";

/**
 * 収穫金倉庫コントローラー
 *
 * 1ページ内でHTML要素を切り替えることで画面遷移を実現するSPA的な実装。
 * 詳細は docs/harvest-warehouse/README.md を参照。
 */
export class HarvestWarehouseController extends Controller {
  static targets = [
    // === Scene targets ===
    // 画面切り替え対象のDOM要素（display: none/block で切り替え）
    "sceneTop",

    "scenePayoutRequestTop",
    "scenePayoutRequestConfirm",
    "scenePayoutRequestHistory",

    "sceneHarvestTop",
    "sceneHarvestHistory",
    "sceneHarvestEstimatedReturn",

    // === Payout request targets ===
    // 出金依頼画面のUI要素
    "payoutRequestInput",
    "payoutRequestPlaceholder",
    "payoutRequestHistoryTabContainer",
    "payoutRequestExecutionContainer",
    "payoutRequestNote",
    "payoutRequestError",
    "confirmAmountDisplay",

    // === Payout request cancel dialog targets ===
    // 履歴キャンセル確認ダイアログ
    "payoutRequestCancelDialog",
    "payoutRequestCancelDate",
    "payoutRequestCancelAmount",

    // === Footer targets ===
    // フッター会話テキスト（ページ送り対応）
    "characterNameTag",
    "footerDialogueTextArea",
    "footerDialogueTextCursorUp",
    "footerDialogueTextCursorDown",

    // === Header targets ===
    "headerTitle",

    // === Payout request history targets ===
    "payoutRequestHistoryTabs",
    "payoutRequestHistoryTabsList",
    "payoutRequestHistoryTabTemplate",
    "payoutRequestHistoryTab",
    "payoutRequestHistoryFrame",
    "payoutRequestHistoryDataContainer",
    "payoutRequestHistoryEmptyContainer",

    // === Harvest history targets ===
    "harvestHistoryTabs",
    "harvestHistoryTabsList",
    "harvestHistoryTabTemplate",
    "harvestHistoryTab",
    "harvestHistoryFrame",
    "harvestHistoryDataContainer",
    "harvestHistoryEmptyContainer",

    // === Estimated return targets ===
    "estimatedReturnGrandTotal",
    "estimatedReturnTabs",
    "estimatedReturnTabTemplate",
    "estimatedReturnTab",
    "estimatedReturnListFrame",
  ];

  static values = {
    // Mypage path
    mypagePath: String,

    // Scene state
    currentScene: String,

    // Server data (raw values for validation)
    availableBalance: Number,
    minimumPayoutAmount: Number,

    // Paths
    submitPayoutRequestPath: String,
    cancelPayoutRequestBasePath: String,
    payoutRequestHistoryPath: String,

    // User input
    payoutRequestAmount: Number,

    // for the dialogue text
    footerDialoguePageTexts: Array,
    currentFooterDialoguePageIndex: { type: Number, default: 0 },

    // Payout request history tabs
    payoutRequestHistoryYears: Array,
    selectedPayoutRequestHistoryYear: Number,

    // Harvest history tabs
    harvestHistoryYears: Array,
    selectedHarvestHistoryYear: Number,
    harvestHistoryPath: String,

    // Estimated return
    estimatedReturnTotalsPath: String,
    estimatedReturnListPath: String,
  };

  connect() {
    this.isFooterDialogueAnimating = false;
    this.payoutInputDialogueShown = false;
    this.characterNameTagTarget.textContent = CHARACTER_NAME;
    this.initializePayoutRequestHistoryTabs();
    this.initializeHarvestHistoryTabs();
    this.switchToSceneTop();
  }

  /**
   * 数値を3桁区切りでフォーマット
   * @param {number|string} value - フォーマット対象の数値
   * @returns {string} フォーマット済み文字列 例: "1,000"
   */
  formatNumberWithDelimiters(value) {
    if (value === null || value === undefined || value === "") return "";
    const num = typeof value === "number" ? value : parseInt(value, 10);
    if (isNaN(num)) return "";
    return num.toLocaleString("ja-JP");
  }

  /**
   * 金額を通貨記号付きでフォーマット（¥1,234,567）
   * @param {number|string} amount
   * @returns {string}
   */
  formatAmountWithCurrency(amount) {
    return `¥${this.formatNumberWithDelimiters(amount)}`;
  }
}

Object.assign(
  HarvestWarehouseController.prototype,
  HeaderManagementMixin,
  PayoutRequestHandlingMixin,
  PayoutRequestCancelHandlingMixin,
  FooterDialogueManagementMixin,
  SceneNavigationMixin,
  TurboHelpersMixin,
  PayoutRequestHistoryTabsManagementMixin,
  HarvestHistoryTabsManagementMixin,
  EstimatedReturnTabsManagementMixin,
);
